import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { checklistProgress, checklistWeights, type TaskView } from '@apm/contracts';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  commitCountByChecklistItem,
  useTaskProgressReports,
} from '../../features/git/components/task-progress-commits';
import { useProgressCommitGuard } from '../../features/git/use-progress-commit-guard';
import { apiFetch } from '../../lib/http';
import { errorMessage } from '../../lib/error-message';
import { cn } from '../../lib/utils';

/** Structured checklist state is authoritative; Markdown remains description prose. */
export function TaskProgressChecklist({ task }: { readonly task: TaskView }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const queryKey = ['task-checklist', task.id] as const;
  const query = useQuery({
    queryKey,
    queryFn: () => apiFetch<TaskView>(`/tasks/${task.id}`),
    initialData: task,
  });
  const current = query.data;
  // Tick một mục ⇒ hỏi commit nếu dự án bật (APM-PCOM-c). Bỏ tick thì không hỏi.
  const commits = useProgressCommitGuard();
  const commitCounts = commitCountByChecklistItem(useTaskProgressReports(task.projectId, task.id));
  const toggle = useMutation({
    mutationFn: ({ itemId, checked }: { itemId: string; checked: boolean }) =>
      apiFetch<TaskView>(`/tasks/${task.id}/checklist/${itemId}`, {
        method: 'PUT',
        body: { checked },
      }),
    onMutate: async ({ itemId, checked }) => {
      await client.cancelQueries({ queryKey });
      const before = client.getQueryData<TaskView>(queryKey);
      if (before?.checklist) {
        const checklist = {
          items: before.checklist.items.map((item) =>
            item.id === itemId ? { ...item, checked } : item,
          ),
        };
        client.setQueryData(queryKey, {
          ...before,
          checklist,
          progressPct: checklistProgress(checklist),
        });
      }
      return { before };
    },
    onError: (error, _input, context) => {
      if (context?.before) client.setQueryData(queryKey, context.before);
      toast.error(errorMessage(error, t));
    },
    onSuccess: (view) => client.setQueryData(queryKey, view),
    onSettled: async () => {
      await client.invalidateQueries({
        predicate: (query) =>
          [
            'task-checklist',
            'board',
            'gantt',
            'backlog',
            'me',
            'tasks',
            'task',
            'collaboration',
            'dashboard',
            'reports',
          ].includes(String(query.queryKey[0])),
      });
    },
  });
  if (!current.checklist) return null;
  const items = current.checklist.items;
  const weights = checklistWeights(items);
  return (
    <section className="flex flex-col gap-2" aria-label={t('checklist.title')}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-medium">{t('checklist.title')}</h4>
        <span className="text-xs tabular-nums" aria-live="polite">
          {t('checklist.summary', {
            done: items.filter((item) => item.checked).length,
            total: items.length,
            progress: current.progressPct,
          })}
        </span>
      </div>
      {/*
        Hai trạng thái khoá được tích, và cả hai đều nói ra **cách mở khoá** —
        một ô checkbox mờ đi mà không giải thích là chỗ người dùng bấm mãi rồi
        đi mở ticket. Task đang làm thì không có dòng nào: câu "tiến độ tự tính
        từ checklist" nói lại đúng thứ hàng ngay trên đã hiện bằng số
        (`checklist.summary`), và một câu giải thích cơ chế đứng cạnh chính con
        số nó giải thích là chữ thừa.
      */}
      {/* Backlog Agile đứng trước: chuyển cột không gỡ được khoá này (`TASK_IN_BACKLOG`). */}
      {current.inBacklog === true ? (
        <p className="text-xs text-muted-foreground">{t('checklist.backlogHelp')}</p>
      ) : current.statusCategory === 'done' ? (
        <p className="text-xs text-muted-foreground">{t('checklist.doneHelp')}</p>
      ) : current.statusCategory === 'todo' ? (
        <p className="text-xs text-muted-foreground">{t('checklist.todoHelp')}</p>
      ) : null}
      <ul className="flex flex-col gap-2">
        {items.map((item, index) => (
          <li key={item.id}>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={item.checked}
                className="ios-check"
                /*
                  "Sẵn sàng" cũng khoá, cùng lý do với "hoàn thành": tích một
                  mục là báo cáo đã làm được phần việc đó, mà việc thì chưa
                  vào luồng đang làm. Tích ở đây sẽ đẩy `progressPct` lên
                  trong khi thẻ vẫn nằm ở cột đầu — burndown và cycle time đọc
                  đúng cặp số đó, nên chúng sẽ nói sai.
                */
                disabled={
                  !current.canToggleChecklist ||
                  current.statusCategory === 'done' ||
                  current.statusCategory === 'todo' ||
                  // Backlog Agile: chưa vào sprint thì chưa báo tiến độ — cửa thật ở server.
                  current.inBacklog === true ||
                  toggle.isPending
                }
                onChange={(event) => {
                  const checked = event.target.checked;
                  if (!checked) {
                    toggle.mutate({ itemId: item.id, checked });
                    return;
                  }
                  commits.ask(
                    {
                      projectId: current.projectId,
                      taskId: current.id,
                      trigger: 'checklist_item',
                      checklistItemId: item.id,
                      heading: t('progressCommits.headingChecklist', { text: item.text }),
                    },
                    () => toggle.mutateAsync({ itemId: item.id, checked }),
                  );
                }}
              />
              <span
                className={cn(
                  'min-w-0 flex-1 whitespace-pre-line break-words',
                  item.checked && 'text-muted-foreground line-through',
                )}
              >
                {item.text}
              </span>
              {commitCounts.get(item.id) ? (
                <span
                  className="shrink-0 rounded-full bg-muted px-1.5 text-[11px] text-muted-foreground"
                  data-testid="checklist-commit-chip"
                >
                  {t('progressCommits.chip', { count: commitCounts.get(item.id) })}
                </span>
              ) : null}
              <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                {weights[index]!.toLocaleString(undefined, { maximumFractionDigits: 2 })}%
              </span>
            </label>
          </li>
        ))}
      </ul>
      {commits.dialog}
    </section>
  );
}
